import Places from 'places.js';
import {getPageCountryCode} from '@malt/dom';
import * as defaultAutosuggestions from './defaultAutosuggestionCities';
import REMOTE_ALLOWED_DROPDOWN_TEMPLATE from '../../../../resources/handlebars/components/location/remote-allowed-dropdown.hbs';

const LOCALE = window.GEOLOCATION_LOCALE;
const PREFERRED_SEARCH_TYPES = 'city';
const BASE_PLACES_API_PARAMS = {
    language: LOCALE,
    type: PREFERRED_SEARCH_TYPES,
};

const ADDRESS_FIELDS_AND_API_MODEL_MAPPING = {
    city: 'name',
    countryCode: 'countryCode',
    country: 'country',
    administrativeAreaLevel1: 'administrative',
    administrativeAreaLevel1Code: 'administrative',
    administrativeAreaLevel2: 'county',
    administrativeAreaLevel2Code: 'postcode',
    administrativeAreaLevel3: '',
    administrativeAreaLevel3Code: '',
    administrativeAreaLevel4: '',
    administrativeAreaLevel4Code: '',
    lon: 'latlng.lng',
    lat: 'latlng.lat',
    remoteAllowed: 'remoteAllowed',
};

const REMOTE_ONLY_DROPDOWN_CLASS = 'location-remote-allowed-dropdown';

Hop.requireMessage('search.location.remote.*');

function getProxyHostsOptions() {
    const location = window.location;
    const host = `${location.host}/algolia`;

    return {
        protocol: location.protocol,
        hosts: {
            write: [host],
            read: [host],
        },
    };
}

function getAdressFieldsRelatedInputs(container, fields) {
    const els = {};

    fields.forEach((f) => {
        els[f] =
            container.querySelector(`input[name="${f}"]`) ||
            container.querySelector(`input[name="address.${f}"]`) ||
            container.querySelector(`input[name="address.loc.${f}"]`);
    });

    return els;
}

function getFieldValueFromSuggestion(field, suggestion, map = ADDRESS_FIELDS_AND_API_MODEL_MAPPING) {
    const path = map[field].split('.');
    return path.reduce((acc, curr) => {
        if (!acc) {
            return;
        }
        return Array.isArray(acc[curr]) ? acc[curr][0] : acc[curr];
    }, suggestion);
}

function SUGGESTION_TPL(suggestion) {
    if (suggestion.type === 'city') {
        const administrative = suggestion.highlight.administrative ? ' ' + suggestion.highlight.administrative + ',' : '';
        return `<span class="fs-main ap-suggestion__content">${suggestion.highlight.name},${administrative} ${suggestion.country}</span>`;
    } else {
        return `<span class="fs-main ap-suggestion__content">${suggestion.highlight.name}</span>`;
    }
}

function INPUT_VALUE_TEMPLATE(suggestion) {
    if (suggestion.type === 'city') {
        return `${suggestion.city || suggestion.name}, ${suggestion.country}`;
    } else {
        return `${suggestion.country}`;
    }
}

export class AlgoliaPlaces {
    constructor(root, {id, inputName, defaultValue, allowRemote = false, localeCountryCode, remoteIfEmpty = false}) {
        this.onInput = this.onInput.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.hideRemoteOnlyDropdown = this.hideRemoteOnlyDropdown.bind(this);
        this.setRemoteAllowed = this.setRemoteAllowed.bind(this);
        this.autosuggestionClicked = this.autosuggestionClicked.bind(this);

        this.id = id;
        this.form = root;
        this.queryInput = inputName
            ? this.form.querySelector(`input[name=${inputName}]`)
            : this.form.querySelector('input[name=location]') || this.form.querySelector('input[name="address.formattedAddress"]');
        this.addressInputs = getAdressFieldsRelatedInputs(this.form, Object.keys(ADDRESS_FIELDS_AND_API_MODEL_MAPPING));
        this.address = defaultValue || this.getAddressBasedOnInputValues();
        this.allowRemote = allowRemote && !!this.remoteAllowedLabel;
        this.localeCountryCode = localeCountryCode;
        this.remoteIfEmpty = remoteIfEmpty;
        // let's be nice with dynamic templates (JSP, JS,...) and don't force them to conditionally include this module
        if (this.queryInput === null) {
            return;
        }

        const localizedCitiesSuggestionList = defaultAutosuggestions[this.localeCountryCode];
        if (localizedCitiesSuggestionList) {
            this.citiesSuggestion = localizedCitiesSuggestionList.map((city) => ({...city, notAlgolia: true}));
        } else {
            this.citiesSuggestion = [];
        }

        const options = {
            container: this.queryInput,
            clientOptions: window.ALGOLIA_PROXY ? getProxyHostsOptions() : null,
            autocompleteOptions: {
                autoselect: true,
                tabAutocomplete: true,
                autoselectOnBlur: true,
            },
            templates: {
                suggestion: SUGGESTION_TPL,
                value: INPUT_VALUE_TEMPLATE,
            },
        };

        const appId = window.ALGOLIA_APP_ID;
        if (appId) {
            options.appId = appId;
        }

        const apiKey = window.ALGOLIA_API_KEY;
        if (apiKey) {
            options.apiKey = apiKey;
        }

        this.autocomplete = Places(Object.assign({}, BASE_PLACES_API_PARAMS, options));

        if (this.allowRemote) {
            this.dropdownElement = this.createRemoteOnlyDropdown();
        }

        this.disableEmptyFields();
        this.listenToUserActions();

        this.onBlur();
    }

    disableEmptyFields() {
        this.addressInputs &&
            Object.keys(this.addressInputs).forEach((key) => {
                const input = this.addressInputs[key];
                if (input) {
                    input.disabled = !input.value;
                }
            });
    }

    getAddressBasedOnInputValues() {
        const address = {};
        Object.keys(ADDRESS_FIELDS_AND_API_MODEL_MAPPING).forEach((f) => {
            address[f] = this.addressInputs[f] ? this.addressInputs[f].value : null;
        });

        return address;
    }

    updateAddress(suggestion, map = ADDRESS_FIELDS_AND_API_MODEL_MAPPING) {
        if (this.address) {
            Object.keys(this.address).forEach((field) => this.updateAddressField({field, suggestion, map}));
            this.address.formattedAddress = INPUT_VALUE_TEMPLATE(suggestion);
            this.address.notAlgolia = suggestion.notAlgolia;
        }
        this.disableEmptyFields();
    }

    updateAddressField({field, suggestion, map}) {
        if (!map[field]) {
            return;
        }
        this.address[field] = getFieldValueFromSuggestion(field, suggestion, map);

        if (field === 'countryCode' && this.address[field]) {
            this.address[field] = this.address[field].toUpperCase();
        }

        if (this.addressInputs[field]) {
            this.addressInputs[field].value = this.address[field];

            if (!this.address[field]) {
                this.addressInputs[field].value = null;
            }
        }
    }

    onBlur() {
        if (this.remoteAllowed) {
            this.queryInput.value = this.remoteAllowedLabel;
        }
        if (this.remoteIfEmpty && !Object.values(this.address).find((value) => !!value)) {
            // the input is empty and the address is not set
            this.setRemoteAllowed();
            this.queryInput.value = this.remoteAllowedLabel;
        }
        if (this.address.notAlgolia) {
            this.queryInput.value = this.address.formattedAddress;
        }
        if (this.queryInput.required) {
            const isAddressValid = this.address && (this.remoteAllowed || (this.address.countryCode && this.address.city));
            if (!isAddressValid) {
                this.queryInput.value = '';
            }
        }
    }

    onInput() {
        if (this.queryInput.value === '') {
            this.displayRemoteAllowedDropdownIfPossible();
        } else {
            this.hideRemoteOnlyDropdown();
        }

        this.resetAddress();
    }

    resetAddress() {
        Object.keys(this.address).forEach((field) => {
            this.address[field] = null;
            if (this.addressInputs[field]) {
                this.addressInputs[field].value = this.address[field];
            }
        });

        this.triggerChangeEvent();
    }

    locationChangeCallBack(e) {
        const suggest = e.suggestion;
        this.updateAddress(suggest);
        this.triggerChangeEvent();
    }

    triggerChangeEvent() {
        const address = this.address && {
            ...this.address,
            loc: {
                lat: this.address.lat,
                lon: this.address.lon,
            },
        };
        Hop.events.trigger('location:place_changed', address, this.id);
    }

    autosuggestionClicked(e) {
        const index = parseInt(e.target.getAttribute('data-city-index'), 10);

        if (!isNaN(index) && index >= 0 && index < this.citiesSuggestion.length) {
            const city = this.citiesSuggestion[index];
            this.updateAddress({...city});
        }
    }

    listenToUserActions() {
        this.autocomplete.on('change', this.locationChangeCallBack.bind(this));

        this.queryInput.addEventListener('input', this.onInput);
        this.queryInput.addEventListener('blur', this.onBlur);

        if (this.allowRemote) {
            this.queryInput.addEventListener('focus', this.onFocus);
            this.queryInput.addEventListener('blur', this.hideRemoteOnlyDropdown);

            this.dropdownElement.querySelectorAll('.location-remote-allowed-dropdown__suggestion-list li').forEach((suggestion) => {
                suggestion.addEventListener('mousedown', this.autosuggestionClicked);
            });

            this.dropdownElement.querySelector('.location-remote-allowed-dropdown__suggestion').addEventListener('mousedown', this.setRemoteAllowed);
        }
    }

    resetData() {
        this.autocomplete = null;
        this.form = null;
        this.queryInput = null;
        this.addressInputs = null;
        this.address = null;
    }

    removeListeners() {
        this.queryInput.removeEventListener('input', this.onInput);
        this.queryInput.removeEventListener('blur', this.onBlur);

        if (this.allowRemote) {
            this.queryInput.removeEventListener('focus', this.onFocus);
            this.queryInput.removeEventListener('blur', this.hideRemoteOnlyDropdown);
            this.dropdownElement.querySelectorAll('.location-remote-allowed-dropdown__suggestion-list li').forEach((suggestion) => {
                suggestion.removeEventListener('mousedown', this.autosuggestionClicked);
            });
            this.dropdownElement
                .querySelector('.location-remote-allowed-dropdown__suggestion')
                .removeEventListener('mousedown', this.setRemoteAllowed);
        }
    }

    get remoteAllowed() {
        return this.address.remoteAllowed === 'true' || this.address.remoteAllowed === true;
    }

    get remoteAllowedLabel() {
        const pageCountryCode = getPageCountryCode();
        return pageCountryCode ? Hop.message(`search.location.remote.${pageCountryCode}`) : null;
    }

    createRemoteOnlyDropdown() {
        let dropdownElement = this.queryInput.parentElement.querySelector(`.${REMOTE_ONLY_DROPDOWN_CLASS}`);

        const citiesLabel = this.citiesSuggestion.map((city) => {
            return {label: city.value};
        });

        if (!dropdownElement) {
            dropdownElement = document.createElement('div');
            dropdownElement.className = REMOTE_ONLY_DROPDOWN_CLASS;
            dropdownElement.style.display = 'none';
            dropdownElement.setAttribute('data-heap', 'location_suggestion');
            this.queryInput.parentElement.appendChild(dropdownElement);
        } else {
            dropdownElement.style.display = 'none';
        }

        dropdownElement.innerHTML = REMOTE_ALLOWED_DROPDOWN_TEMPLATE({
            cities: citiesLabel,
            label: this.remoteAllowedLabel,
        });

        return dropdownElement;
    }

    onFocus() {
        this.displayRemoteAllowedDropdownIfPossible();
        if (this.remoteAllowed) {
            this.queryInput.value = '';
        }
    }

    displayRemoteAllowedDropdownIfPossible() {
        if (
            this.dropdownElement &&
            // let's display the remote option only if places suggestions are not already displayed
            !this.queryInput.parentElement.querySelector('.ap-suggestions')
        ) {
            this.dropdownElement.style.display = 'block';
        }
    }

    hideRemoteOnlyDropdown() {
        if (this.dropdownElement) {
            this.dropdownElement.style.display = 'none';
        }
    }

    setRemoteAllowed() {
        this.updateAddress({remoteAllowed: true});
    }

    destroy() {
        this.removeListeners();
        this.resetData();
    }
}
